import { sql } from "drizzle-orm";
import {
  check,
  foreignKey,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { organisations, schoolPupils, students, tutorProfiles, users } from "./schemaBase.js";

/**
 * Hub-owned group classes and workshops. This table does not extend Amelia's
 * one-to-one appointments or the canonical School lesson domain. Enrolment,
 * attendance and wait-list state are added in separate reviewed slices.
 */
export const GROUP_ACTIVITY_TYPES = ["group_lesson", "workshop", "ensemble", "other"] as const;
export type GroupActivityType = (typeof GROUP_ACTIVITY_TYPES)[number];

export const GROUP_ACTIVITY_DELIVERY_MODES = ["in_person", "online"] as const;
export type GroupActivityDeliveryMode = (typeof GROUP_ACTIVITY_DELIVERY_MODES)[number];

export const GROUP_ACTIVITY_STATUSES = ["draft", "open", "cancelled", "completed"] as const;
export type GroupActivityStatus = (typeof GROUP_ACTIVITY_STATUSES)[number];

export const groupActivities = pgTable(
  "group_activities",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull(),
    title: text("title").notNull(),
    activityType: text("activity_type").$type<GroupActivityType>().notNull(),
    description: text("description"),
    deliveryMode: text("delivery_mode")
      .$type<GroupActivityDeliveryMode>()
      .notNull()
      .default("in_person"),
    venue: text("venue"),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    eligibilityNotes: text("eligibility_notes"),
    capacity: integer("capacity").notNull(),
    status: text("status").$type<GroupActivityStatus>().notNull().default("draft"),
    assignedTutorProfileId: varchar("assigned_tutor_profile_id"),
    createdByUserId: varchar("created_by_user_id").notNull(),
    updatedByUserId: varchar("updated_by_user_id"),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
    version: integer("version").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    foreignKey({
      name: "group_activities_organisation_fk",
      columns: [table.organisationId],
      foreignColumns: [organisations.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activities_assigned_tutor_fk",
      columns: [table.assignedTutorProfileId],
      foreignColumns: [tutorProfiles.id],
    }).onDelete("set null"),
    foreignKey({
      name: "group_activities_creator_user_fk",
      columns: [table.createdByUserId],
      foreignColumns: [users.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activities_updater_user_fk",
      columns: [table.updatedByUserId],
      foreignColumns: [users.id],
    }).onDelete("set null"),
    // Tenant guards complement the nullable single-column FKs above. Once an
    // optional reference is cleared on delete, MATCH SIMPLE skips that pair.
    foreignKey({
      name: "group_activities_assigned_tutor_org_fk",
      columns: [table.organisationId, table.assignedTutorProfileId],
      foreignColumns: [tutorProfiles.organisationId, tutorProfiles.id],
    }),
    foreignKey({
      name: "group_activities_creator_user_org_fk",
      columns: [table.organisationId, table.createdByUserId],
      foreignColumns: [users.organisationId, users.id],
    }),
    foreignKey({
      name: "group_activities_updater_user_org_fk",
      columns: [table.organisationId, table.updatedByUserId],
      foreignColumns: [users.organisationId, users.id],
    }),
    uniqueIndex("group_activities_org_creator_key_idx").on(
      table.organisationId,
      table.createdByUserId,
      table.idempotencyKey,
    ),
    // Supports tenant-scoped composite references from the enrolment slice.
    uniqueIndex("group_activities_org_id_idx").on(table.organisationId, table.id),
    index("group_activities_org_starts_idx").on(table.organisationId, table.startsAt),
    index("group_activities_org_status_start_idx").on(
      table.organisationId,
      table.status,
      table.startsAt,
    ),
    check("group_activities_title_check", sql.raw("length(trim(title)) between 1 and 200")),
    check(
      "group_activities_type_check",
      sql.raw("activity_type in ('group_lesson','workshop','ensemble','other')"),
    ),
    check(
      "group_activities_description_check",
      sql.raw("description is null or length(trim(description)) <= 2000"),
    ),
    check(
      "group_activities_delivery_mode_check",
      sql.raw("delivery_mode in ('in_person','online')"),
    ),
    check(
      "group_activities_venue_check",
      sql.raw(
        "(delivery_mode = 'in_person' and venue is not null and length(trim(venue)) between 1 and 200) " +
          "or (delivery_mode = 'online' and (venue is null or length(trim(venue)) between 1 and 200))",
      ),
    ),
    check("group_activities_time_order_check", sql.raw("ends_at > starts_at")),
    check(
      "group_activities_eligibility_check",
      sql.raw("eligibility_notes is null or length(trim(eligibility_notes)) <= 1000"),
    ),
    check("group_activities_capacity_check", sql.raw("capacity between 1 and 10000")),
    check(
      "group_activities_status_check",
      sql.raw("status in ('draft','open','cancelled','completed')"),
    ),
    check("group_activities_idempotency_key_check", sql.raw("idempotency_key ~* '^[0-9a-f-]{36}$'")),
    check("group_activities_request_fingerprint_check", sql.raw("request_fingerprint ~ '^[0-9a-f]{64}$'")),
    check("group_activities_version_check", sql.raw("version > 0")),
  ],
);


/** Learner identities remain within their canonical teaching domain. */
export const GROUP_ACTIVITY_LEARNER_DOMAINS = ["private", "school"] as const;
export type GroupActivityLearnerDomain = (typeof GROUP_ACTIVITY_LEARNER_DOMAINS)[number];

export const GROUP_ACTIVITY_ENROLMENT_STATUSES = ["enrolled", "withdrawn", "cancelled"] as const;
export type GroupActivityEnrolmentStatus = (typeof GROUP_ACTIVITY_ENROLMENT_STATUSES)[number];

/**
 * A free administrative reservation for one learner in one Hub group activity.
 * Payment, credit, attendance, wait-list and outbound notification state live
 * outside this first enrolment/capacity slice.
 */
export const groupActivityEnrollments = pgTable(
  "group_activity_enrollments",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull(),
    activityId: varchar("activity_id").notNull(),
    learnerDomain: text("learner_domain")
      .$type<GroupActivityLearnerDomain>()
      .notNull()
      .default("private"),
    privateStudentId: varchar("private_student_id"),
    schoolPupilId: varchar("school_pupil_id"),
    createdByUserId: varchar("created_by_user_id").notNull(),
    status: text("status").$type<GroupActivityEnrolmentStatus>().notNull().default("enrolled"),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
    enrolledAt: timestamp("enrolled_at", { withTimezone: true }).notNull().defaultNow(),
    withdrawnAt: timestamp("withdrawn_at", { withTimezone: true }),
    cancelledAt: timestamp("cancelled_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    foreignKey({
      name: "group_activity_enrollments_activity_org_fk",
      columns: [table.organisationId, table.activityId],
      foreignColumns: [groupActivities.organisationId, groupActivities.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_enrollments_private_student_org_fk",
      columns: [table.organisationId, table.privateStudentId],
      foreignColumns: [students.organisationId, students.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_enrollments_school_pupil_org_fk",
      columns: [table.organisationId, table.schoolPupilId],
      foreignColumns: [schoolPupils.organisationId, schoolPupils.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_enrollments_creator_org_fk",
      columns: [table.organisationId, table.createdByUserId],
      foreignColumns: [users.organisationId, users.id],
    }).onDelete("restrict"),
    uniqueIndex("group_activity_enrollments_org_id_idx").on(table.organisationId, table.id),
    uniqueIndex("group_activity_enrollments_creator_key_idx").on(
      table.organisationId,
      table.createdByUserId,
      table.idempotencyKey,
    ),
    uniqueIndex("group_activity_enrollments_private_active_idx")
      .on(table.organisationId, table.activityId, table.privateStudentId)
      .where(sql.raw("learner_domain = 'private' and status = 'enrolled'")),
    uniqueIndex("group_activity_enrollments_school_active_idx")
      .on(table.organisationId, table.activityId, table.schoolPupilId)
      .where(sql.raw("learner_domain = 'school' and status = 'enrolled'")),
    index("group_activity_enrollments_activity_status_idx").on(
      table.organisationId,
      table.activityId,
      table.status,
    ),
    index("group_activity_enrollments_private_student_idx").on(
      table.organisationId,
      table.privateStudentId,
      table.createdAt,
    ),
    index("group_activity_enrollments_school_pupil_idx").on(
      table.organisationId,
      table.schoolPupilId,
      table.createdAt,
    ),
    check(
      "group_activity_enrollments_learner_identity_check",
      sql.raw(
        "(learner_domain = 'private' and private_student_id is not null and school_pupil_id is null) " +
          "or (learner_domain = 'school' and school_pupil_id is not null and private_student_id is null)",
      ),
    ),
    check(
      "group_activity_enrollments_status_check",
      sql.raw("status in ('enrolled', 'withdrawn', 'cancelled')"),
    ),
    check(
      "group_activity_enrollments_idempotency_check",
      sql.raw("idempotency_key ~* '^[0-9a-f-]{36}$'"),
    ),
    check(
      "group_activity_enrollments_fingerprint_check",
      sql.raw("request_fingerprint ~ '^[0-9a-f]{64}$'"),
    ),
    check(
      "group_activity_enrollments_state_time_check",
      sql.raw(
        "(status <> 'withdrawn' or withdrawn_at is not null) and " +
          "(status <> 'cancelled' or cancelled_at is not null)",
      ),
    ),
  ],
);


export const GROUP_ACTIVITY_WAITLIST_STATUSES = [
  "waiting",
  "promoted",
  "withdrawn",
  "cancelled",
  "ineligible",
] as const;
export type GroupActivityWaitlistStatus = (typeof GROUP_ACTIVITY_WAITLIST_STATUSES)[number];

/**
 * A private FIFO queue for a free group-activity place. Promotion behaviour
 * is gated separately until the owner-approved confirmation and communication
 * policy is ready.
 */
export const groupActivityWaitlistEntries = pgTable(
  "group_activity_waitlist_entries",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull(),
    activityId: varchar("activity_id").notNull(),
    learnerDomain: text("learner_domain")
      .$type<GroupActivityLearnerDomain>()
      .notNull()
      .default("private"),
    privateStudentId: varchar("private_student_id"),
    schoolPupilId: varchar("school_pupil_id"),
    createdByUserId: varchar("created_by_user_id").notNull(),
    status: text("status").$type<GroupActivityWaitlistStatus>().notNull().default("waiting"),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    promotedAt: timestamp("promoted_at", { withTimezone: true }),
    withdrawnAt: timestamp("withdrawn_at", { withTimezone: true }),
    cancelledAt: timestamp("cancelled_at", { withTimezone: true }),
    invalidatedAt: timestamp("invalidated_at", { withTimezone: true }),
  },
  (table) => [
    foreignKey({
      name: "group_activity_waitlist_activity_org_fk",
      columns: [table.organisationId, table.activityId],
      foreignColumns: [groupActivities.organisationId, groupActivities.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_waitlist_private_student_org_fk",
      columns: [table.organisationId, table.privateStudentId],
      foreignColumns: [students.organisationId, students.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_waitlist_school_pupil_org_fk",
      columns: [table.organisationId, table.schoolPupilId],
      foreignColumns: [schoolPupils.organisationId, schoolPupils.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_waitlist_creator_org_fk",
      columns: [table.organisationId, table.createdByUserId],
      foreignColumns: [users.organisationId, users.id],
    }).onDelete("restrict"),
    uniqueIndex("group_activity_waitlist_creator_key_idx").on(
      table.organisationId,
      table.createdByUserId,
      table.idempotencyKey,
    ),
    uniqueIndex("group_activity_waitlist_private_active_idx")
      .on(table.organisationId, table.activityId, table.privateStudentId)
      .where(sql.raw("learner_domain = 'private' and status = 'waiting'")),
    uniqueIndex("group_activity_waitlist_school_active_idx")
      .on(table.organisationId, table.activityId, table.schoolPupilId)
      .where(sql.raw("learner_domain = 'school' and status = 'waiting'")),
    index("group_activity_waitlist_fifo_idx").on(
      table.organisationId,
      table.activityId,
      table.status,
      table.createdAt,
      table.id,
    ),
    index("group_activity_waitlist_private_student_idx").on(
      table.organisationId,
      table.privateStudentId,
      table.createdAt,
    ),
    index("group_activity_waitlist_school_pupil_idx").on(
      table.organisationId,
      table.schoolPupilId,
      table.createdAt,
    ),
    check(
      "group_activity_waitlist_learner_identity_check",
      sql.raw(
        "(learner_domain = 'private' and private_student_id is not null and school_pupil_id is null) " +
          "or (learner_domain = 'school' and school_pupil_id is not null and private_student_id is null)",
      ),
    ),
    check(
      "group_activity_waitlist_status_check",
      sql.raw("status in ('waiting', 'promoted', 'withdrawn', 'cancelled', 'ineligible')"),
    ),
    check("group_activity_waitlist_idempotency_check", sql.raw("idempotency_key ~* '^[0-9a-f-]{36}$'")),
    check("group_activity_waitlist_fingerprint_check", sql.raw("request_fingerprint ~ '^[0-9a-f]{64}$'")),
    check(
      "group_activity_waitlist_state_time_check",
      sql.raw(
        "(status <> 'promoted' or promoted_at is not null) and " +
          "(status <> 'withdrawn' or withdrawn_at is not null) and " +
          "(status <> 'cancelled' or cancelled_at is not null) and " +
          "(status <> 'ineligible' or invalidated_at is not null)",
      ),
    ),
  ],
);


export const GROUP_ACTIVITY_ATTENDANCE_STATUSES = ["present", "absent", "late"] as const;
export type GroupActivityAttendanceStatus = (typeof GROUP_ACTIVITY_ATTENDANCE_STATUSES)[number];

/**
 * One current attendance mark per group-activity enrolment.
 * Version changes provide stale-write protection; the actor and timestamp
 * record who last marked it without exposing the roster to families.
 */
export const groupActivityAttendance = pgTable(
  "group_activity_attendance",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull(),
    enrollmentId: varchar("enrollment_id").notNull(),
    status: text("status").$type<GroupActivityAttendanceStatus>().notNull(),
    markedByUserId: varchar("marked_by_user_id").notNull(),
    version: integer("version").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    foreignKey({
      name: "group_activity_attendance_enrollment_org_fk",
      columns: [table.organisationId, table.enrollmentId],
      foreignColumns: [groupActivityEnrollments.organisationId, groupActivityEnrollments.id],
    }).onDelete("restrict"),
    foreignKey({
      name: "group_activity_attendance_marker_org_fk",
      columns: [table.organisationId, table.markedByUserId],
      foreignColumns: [users.organisationId, users.id],
    }).onDelete("restrict"),
    uniqueIndex("group_activity_attendance_enrollment_idx").on(
      table.organisationId,
      table.enrollmentId,
    ),
    index("group_activity_attendance_marker_idx").on(
      table.organisationId,
      table.markedByUserId,
      table.updatedAt,
    ),
    check(
      "group_activity_attendance_status_check",
      sql.raw("status in ('present', 'absent', 'late')"),
    ),
    check("group_activity_attendance_version_check", sql.raw("version > 0")),
  ],
);
